import { useEffect, useRef, useState } from 'react';
import { FormInputProps } from '@/@types/form.types';
import EListPopupPickerWithSearch from '@/components/everridge/EListPopupPickerWithSearch';
import { useOrderApartmentsLookupApi } from '@/features/orders/hooks/useOrderApartmentsLookupApi';

type Props<T extends {}> = {
	selectVariant?: 'single' | 'multi';
	showSearch?: boolean;
	icon?: React.FC<React.SVGProps<SVGSVGElement>>;
	disabled?: boolean;
} & FormInputProps<T>;

const OrderApartmentsLookup = <T extends {}>(props: Props<T>) => {
	const [searchValue, setSearchValue] = useState('');
	const { placeholder = 'Select apartment', label = 'Apartments' } = props;

	const loaderRef = useRef<HTMLDivElement>(null);

	const {
		orderApartmentsLookupList,
		fetchOrderApartmentsLookupListNextPage,
		hasOrderApartmentsLookupListNextPage,
		isFetchingOrderApartmentsLookupListNextPage,
	} = useOrderApartmentsLookupApi({ search: searchValue });

	useEffect(() => {
		if (
			!loaderRef.current ||
			!hasOrderApartmentsLookupListNextPage ||
			isFetchingOrderApartmentsLookupListNextPage
		)
			return;

		const scrollableContainer =
			loaderRef.current.closest('.overflow-y-scroll');

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					fetchOrderApartmentsLookupListNextPage();
				}
			},
			{
				root: scrollableContainer,
				rootMargin: '100px',
			},
		);

		const current = loaderRef.current;
		observer.observe(current);

		return () => observer.unobserve(current);
	}, [
		fetchOrderApartmentsLookupListNextPage,
		hasOrderApartmentsLookupListNextPage,
		isFetchingOrderApartmentsLookupListNextPage,
	]);

	return (
		<EListPopupPickerWithSearch
			{...props}
			label={label}
			placeholder={placeholder}
			list={orderApartmentsLookupList ?? []}
			searchValue={searchValue}
			setSearchValue={setSearchValue}
			ref={loaderRef}
		/>
	);
};

export default OrderApartmentsLookup;
